import { Extension } from '@tiptap/core';
import { Node } from '@tiptap/pm/model';
import { Plugin, PluginKey } from '@tiptap/pm/state';

import { getPlaceholderNodes } from './field-mapping';

const VARIABLE_TAG = 'field-mapped-property';

const EXTENSION_NAME = 'singleVariableOrChips';

/**
 * Editor-level invariant for `FieldMappingSelectMulti`:
 *
 * - At most one variable node may exist at any time. A newly inserted
 *   variable replaces any existing one.
 * - Variable and chip placeholders may not coexist. The newcomer wins —
 *   a fresh chip clears the variable; a fresh variable clears all chips.
 *
 * Implemented as `appendTransaction` so the cleanup is its own undo step
 * rather than getting bundled into the user's action. ProseMirror batches
 * the user transaction and our follow-up before painting, so the
 * forbidden mixed state is never visible.
 */
export const SingleVariableOrChips = Extension.create({
  name: EXTENSION_NAME,
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey(EXTENSION_NAME),
        appendTransaction: (transactions, oldState, newState) => {
          if (!transactions.some((transaction) => transaction.docChanged)) return null;

          const previousNodes = getPlaceholderNodes(oldState);
          const newNodes = getPlaceholderNodes(newState);
          const addedNode = newNodes.find(
            (node) => previousNodes.findIndex((existing) => existing === node) === -1,
          );
          if (!addedNode) return null;

          const isVariable = (node: Node) => node.type.name === VARIABLE_TAG;
          const desiredNodes = isVariable(addedNode)
            ? [addedNode]
            : newNodes.filter((node) => !isVariable(node));

          if (desiredNodes.length === newNodes.length) return null;

          const tr = newState.tr;
          tr.deleteRange(0, newState.doc.content.size - 1);
          let position = 1;
          desiredNodes.forEach((node) => {
            tr.insert(position, node);
            position += node.nodeSize;
          });
          return tr;
        },
      }),
    ];
  },
});
